import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  COLOUR_TABLES, CUSTOM_TABLE, MIN_LEVELS, MAX_LEVELS, blendCustom, opacityPreset, presetHex, resizeCustom, resizeOpacity,
} from '../lib/model-colour';

const btn = 'flex-1 px-1 py-1 rounded text-[10px] cursor-pointer border';
const num = 'w-full text-[10px] rounded px-1 py-0.5';

/** Contour levels block of the Volume tools: level count, spacing, min/max, colour table, banded colours, opacity by colour. */
export default function VolumeColourControls({ colour, setColour, range, scale }) {
  const { C } = useTheme();
  const field = { background: C.bg3, color: C.text1, border: `1px solid ${C.border}` };
  const patch = (p) => setColour(p);
  const bound = (key) => (e) => {
    const v = parseFloat(e.target.value);
    patch({ [key]: Number.isFinite(v) && v > 0 ? v : null });
  };
  const strip = scale?.table
    ? `linear-gradient(to right, ${scale.table.map((c) => `rgb(${c.map(Math.round).join(',')})`).join(',')})`
    : 'none';
  const levels = scale?.levels || [];
  const opacity = resizeOpacity(colour.opacity, levels.length);
  const setOpacity = (arr) => patch({ opacity: arr });
  const setOne = (i, v) => setOpacity(opacity.map((a, k) => (k === i ? v : a)));
  const n = levels.length || Math.max(MIN_LEVELS, Math.min(MAX_LEVELS, colour.count));
  const custom = resizeCustom(colour.custom, n) || presetHex(colour.table === CUSTOM_TABLE ? 'ei-rainbow' : colour.table, n);
  const [anchors, setAnchors] = React.useState([1, n]);
  const [from, to] = [Math.min(anchors[0], n), Math.min(anchors[1], n)];
  const setCount = (count) => patch(colour.table === CUSTOM_TABLE ? { count, custom: resizeCustom(custom, count) } : { count });
  const pickTable = (id) => patch(id === CUSTOM_TABLE ? { table: id, custom } : { table: id });
  const setSwatch = (i, v) => patch({ custom: custom.map((c, k) => (k === i ? v : c)) });
  const presets = [['All opaque', 'all'], ['Hide low half', 'low'], ['Hide high half', 'high']];
  return (
    <div data-testid="colour-controls">
      <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Levels: {colour.count} (colours)</div>
      <input type="range" aria-label="Number of contour levels" min={MIN_LEVELS} max={MAX_LEVELS} step={1} value={colour.count}
        onChange={(e) => setCount(parseInt(e.target.value, 10))} className="w-full accent-cyan-400" />
      <div className="flex gap-1 my-1" role="group" aria-label="Level spacing">
        {[[false, 'Linear'], [true, 'Log10']].map(([log, label]) => (
          <button key={label} type="button" aria-pressed={colour.log === log} onClick={() => patch({ log })}
            className={`${btn} ${colour.log === log ? 'bg-accent-soft text-accent border-accent-soft' : 'border-transparent'}`}
            style={colour.log === log ? undefined : { background: C.bg3, color: C.text2 }}>{label}</button>
        ))}
      </div>
      <div className="flex gap-1 mb-1">
        <label className="flex-1 text-[10px]" style={{ color: C.text2 }}>Min
          <input type="number" aria-label="Colour minimum" className={num} style={field} min={0} step="any"
            value={colour.min ?? ''} placeholder={range ? range[0].toPrecision(3) : ''} onChange={bound('min')} />
        </label>
        <label className="flex-1 text-[10px]" style={{ color: C.text2 }}>Max
          <input type="number" aria-label="Colour maximum" className={num} style={field} min={0} step="any"
            value={colour.max ?? ''} placeholder={range ? range[1].toPrecision(3) : ''} onChange={bound('max')} />
        </label>
      </div>
      <select value={colour.table} aria-label="Colour table" onChange={(e) => pickTable(e.target.value)}
        className="w-full text-[10px] mb-1 rounded px-1 py-1" style={field}>
        {COLOUR_TABLES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        <option value={CUSTOM_TABLE}>Custom…</option>
      </select>
      <div data-testid="colour-strip" style={{ height: 8, borderRadius: 2, background: strip, marginBottom: 4 }} />
      {colour.table === CUSTOM_TABLE && (
        <div data-testid="custom-colours" className="mb-2">
          <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Click a colour block to change it</div>
          <div className="flex" style={{ gap: 1 }}>
            {custom.map((c, i) => (
              <input key={i} type="color" value={c} data-testid="custom-swatch" aria-label={`Colour of level ${i + 1}`}
                onChange={(e) => setSwatch(i, e.target.value)}
                style={{ flex: '1 1 0', minWidth: 0, width: 0, height: 22, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
            ))}
          </div>
          <div className="flex gap-1 items-center mt-1 text-[10px]" style={{ color: C.text2 }}>
            <span>Blend</span>
            {[[0, 'Blend from level'], [1, 'Blend to level']].map(([k, label]) => (
              <select key={k} aria-label={label} value={anchors[k]} onChange={(e) => setAnchors(k ? [anchors[0], +e.target.value] : [+e.target.value, anchors[1]])}
                className="rounded px-1 py-0.5 text-[10px]" style={field}>
                {custom.map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
              </select>
            ))}
            <button type="button" onClick={() => patch({ custom: blendCustom(custom, from - 1, to - 1) })}
              className={`${btn} border-transparent`} style={{ background: C.bg3, color: C.text2 }}>Blend</button>
          </div>
          <select aria-label="Start from" value="" onChange={(e) => e.target.value && patch({ custom: presetHex(e.target.value, n) })}
            className="w-full text-[10px] mt-1 rounded px-1 py-1" style={field}>
            <option value="">Start from…</option>
            {COLOUR_TABLES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      )}
      <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Levels apply to all drawn models (blocks, fences, cutting plane).</div>
      <label className="flex items-center gap-2 text-[10px] cursor-pointer" style={{ color: C.text1 }}>
        <input type="checkbox" checked={colour.banded} onChange={(e) => patch({ banded: e.target.checked })} className="accent-cyan-400" />
        Banded colours
      </label>
      {levels.length > 0 && (
        <div data-testid="opacity-curve" className="mt-2">
          <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Opacity by colour</div>
          <div className="flex items-end" style={{ gap: 1 }}>
            {levels.map((v, i) => {
              const c = scale.table[i] || [128, 128, 128];
              return (
                <input key={i} type="range" min={0} max={1} step={0.05} value={opacity[i]} data-testid="opacity-level"
                  aria-label={`Opacity of level ${v.toPrecision(3)} Ω·m`} title={`${v.toPrecision(3)} Ω·m: ${Math.round(opacity[i] * 100)} %`}
                  onChange={(e) => setOne(i, parseFloat(e.target.value))}
                  style={{ writingMode: 'vertical-lr', direction: 'rtl', flex: '1 1 0', minWidth: 0, width: 0, height: 56, margin: 0, padding: 0, accentColor: `rgb(${c.map(Math.round).join(',')})` }} />
              );
            })}
          </div>
          <div className="flex gap-1 mt-1">
            {presets.map(([label, kind]) => (
              <button key={kind} type="button" onClick={() => setOpacity(opacityPreset(kind, levels.length))}
                className={`${btn} border-transparent`} style={{ background: C.bg3, color: C.text2 }}>{label}</button>
            ))}
            <button type="button" onClick={() => setOpacity(null)} className={`${btn} border-transparent`}
              style={{ background: C.bg3, color: C.text2 }}>Reset</button>
          </div>
        </div>
      )}
    </div>
  );
}
